import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { Actions, ofType } from '@ngrx/effects';
import { map } from 'rxjs/operators';
import { SyncActions } from '../../../state/location-list-actions';
import { Store } from '@ngrx/store';
import { selectDatasourceSyncDetails } from '../../../state/location-list-selectors';
import { Observable } from 'rxjs';
import { DatasourceSyncStatus } from '../../../../../../models/datasource-sync-details';
import * as Utils from './restart-datasync-utils';

export interface RestartDatasyncState {
  syncStatusLoading: boolean;
  restartDatasyncLoading: boolean;
}

export interface DatasourceSyncViewModel {
  syncStatusLoading: boolean,
  restartDatasyncLoading: boolean,
  syncStatus: DatasourceSyncStatus,
  syncProgress: string,
  syncProgressNumber: number,
  restartTime: string,
  updateTime: string,
  restartDisabled: boolean,
  dataSyncColorAndSvg: { colorClass: string, svgIcon: string },
  dataSyncProgressBarClass: string,
  elapsedTime: string,
}

@Injectable()
export class RestartDatasyncLocalStoreService extends ComponentStore<RestartDatasyncState> {

  constructor(private actions: Actions, private store: Store) {
    super({
      syncStatusLoading: false,
      restartDatasyncLoading: false,
    })
    this.handleSyncStatusLoading$(this.actions);
    this.handleRestartDatasyncLoading$(this.actions);
    this.store.dispatch(SyncActions.getDatasourceSyncDetails({}));
  }

  readonly viewModel$: Observable<DatasourceSyncViewModel> = this.select(this.state$,
      this.store.select(selectDatasourceSyncDetails), (localState, datasourceSyncDetails) => ({
        syncStatusLoading: localState.syncStatusLoading,
        restartDatasyncLoading: localState.restartDatasyncLoading,
        syncStatus: datasourceSyncDetails?.syncStatus,
        syncProgress: datasourceSyncDetails?.syncProgress,
        syncProgressNumber: Utils.getNumberFromPercentageString(datasourceSyncDetails?.syncProgress),
        restartTime: datasourceSyncDetails?.restartTime,
        updateTime: datasourceSyncDetails?.updateTime,
        restartDisabled: Utils.isRestartDisabled(localState.syncStatusLoading,
            localState.restartDatasyncLoading,
            datasourceSyncDetails?.syncStatus),
        dataSyncColorAndSvg: Utils.getDataSyncColorAndSvg(datasourceSyncDetails?.syncStatus),
        dataSyncProgressBarClass: Utils.getDataSyncProgressBarClass(datasourceSyncDetails?.syncStatus),
        elapsedTime: Utils.getElapsedTime(datasourceSyncDetails?.restartTime, datasourceSyncDetails?.updateTime)
      }));


  // UPDATERS


  readonly updateSyncStatusLoading = this.updater((
      state, syncStatusLoading: boolean): RestartDatasyncState =>
      ({ ...state, syncStatusLoading }));

  readonly updateRestartDatasyncLoading = this.updater((
      state, restartDatasyncLoading: boolean): RestartDatasyncState =>
      ({ ...state, restartDatasyncLoading }));


  // EFFECTS


  readonly handleSyncStatusLoading$ = this.effect((actions$: Actions) => {
    return actions$.pipe(ofType(
            SyncActions.getDatasourceSyncDetails,
            SyncActions.getDatasourceSyncDetailsSuccess,
            SyncActions.getDatasourceSyncDetailsError,
        ),
        map((action) => {
          switch (action.type) {
            case SyncActions.getDatasourceSyncDetails.type:
              this.updateSyncStatusLoading(true);
              break;
            case SyncActions.getDatasourceSyncDetailsSuccess.type:
              this.updateSyncStatusLoading(false);
              break;
            case SyncActions.getDatasourceSyncDetailsError.type:
              this.updateSyncStatusLoading(false);
              break;
          }
        }));
  });

  readonly handleRestartDatasyncLoading$ = this.effect((actions$: Actions) => {
    return actions$.pipe(ofType(
            SyncActions.restartDatasourceSync,
            SyncActions.restartDatasourceSyncSuccess,
            SyncActions.restartDatasourceSyncError,
        ),
        map((action) => {
          switch (action.type) {
            case SyncActions.restartDatasourceSync.type:
              // start the loading bar only once the API call is triggered
              if (!action.triggeredByUser) {
                this.updateRestartDatasyncLoading(true);
              }
              break;
            case SyncActions.restartDatasourceSyncSuccess.type:
              this.updateRestartDatasyncLoading(false);
              break;
            case SyncActions.restartDatasourceSyncError.type:
              this.updateRestartDatasyncLoading(false);
              break;
          }
        }));
  });

}
